import React, { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Button, Dropdown, Image, Upload } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { MoreVert } from "@mui/icons-material";
import * as yup from "yup";
import { useToast } from "@chakra-ui/react";
import useAxios from "../../utils/useAxios";
import { MAX_GALLERY_IMAGES } from "../../config/uploadLimits";
import {
  createLimitedBeforeUpload,
  getImageUploadLimitState,
} from "../../utils/imageUploadLimit";
import UploadingImageTile from "../ui/UploadingImageTile";
import EquipmentAutocomplete from "../Assets/EquipmentAutocomplete";
import TierAutocomplete from "../Assets/TierAutocomplete";
import PanelComponentAutocomplete from "../Assets/PanelComponentAutocomplete";

// yup's `.number()` casts an empty string to NaN and rejects it even with
// `.nullable()` - `.nullable()` only accepts an explicit `null`. Every one
// of these fields starts as `""` on an instrument that simply doesn't have
// that value set (most don't have a PLC/P&ID/IO Panel/comms protocol), so
// without this transform, validate() silently threw a ValidationError on
// nearly every real instrument. None of the affected fields render
// `errors.<field>` in the form below, so the failure was invisible: no
// toast, no red field, no PATCH request - the Update button just appeared
// to do nothing.
const numberOrNull = yup
  .number()
  .transform((value, originalValue) =>
    originalValue === "" || originalValue == null ? null : value,
  )
  .nullable();

const instrumentSchema = yup.object().shape({
  tagNumber: yup.string().required("Tag Number is required"),
  instrumentType: numberOrNull,
  communicationProtocol: numberOrNull,
  description: yup.string(),
  plc: numberOrNull,
  pid: numberOrNull,
  ioPanel: numberOrNull,
  card: yup.string(),
});

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export default function EditInstrument({
  instrument,
  open,
  onClose,
  onSuccess,
  siteId,
  plantId,
  equipmentId,
  tierId,
  componentId,
}) {
  const api = useAxios();
  const toast = useToast();

  // ── Stable instrument ref ────────────────────────────────────────────────
  // Keeps the last non-null instrument so the modal header/body never blanks
  // out during the closing animation when the parent sets instrument → null.
  const stableInstrumentRef = useRef(instrument);
  if (instrument) stableInstrumentRef.current = instrument;
  const stableInstrument = stableInstrumentRef.current;

  const [values, setValues] = useState({
    tagNumber: "",
    instrumentType: "",
    communicationProtocol: "",
    description: "",
    plc: "",
    pid: "",
    ioPanel: "",
    card: "",
    channel: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [flags, setFlags] = useState([]);

  // "Parent Assets"-style cross-links - same concept as EditTier/
  // EditComponent's "Parent Assets" picker, at all three levels an
  // Instrument can relate to (on top of, not instead of, its single
  // structural equipment/tier/component FKs below).
  const [relatedEquipment, setRelatedEquipment] = useState([]);
  const [relatedTiers, setRelatedTiers] = useState([]);
  const [relatedComponents, setRelatedComponents] = useState([]);

  // API-fetched dropdown options.
  // PLC and P&ID come from the same Equipment table — we fetch once and
  // split client-side to save a round-trip.
  const [instrumentTypes, setInstrumentTypes] = useState([]);
  const [commProtocols, setCommProtocols] = useState([]);
  const [plcOptions, setPlcOptions] = useState([]);
  const [pidOptions, setPidOptions] = useState([]);
  const [ioPanelOptions, setIoPanelOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState(false);

  // Images
  const [instrumentImages, setInstrumentImages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [uploadingImages, setUploadingImages] = useState([]);

  // ── Fetch all dropdown options (fully parallel, abortable) ───────────────
  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchOptions = async () => {
      setLoadingOptions(true);
      setOptionsError(false);

      // One equipment request for the whole plant — split into PLC / P&ID below.
      // NOTE: this only filters correctly when the caller actually passes a
      // plantId prop. Callers that render EditInstrument without one (e.g. a
      // higher-level page that hasn't wired plantId through) will get back
      // every piece of equipment company-wide, not just this plant's.
      const equipParams = new URLSearchParams();
      if (plantId) equipParams.set("plant", plantId);

      // TierViewSet filters by the relatedTo M2M via ?relatedTo=<id>.
      // The previous code used ?equipment= which is NOT a recognised filter
      // on that viewset and silently returned the entire unfiltered tier table.
      // When there's no equipment context yet, fall back to scoping tiers by
      // plant instead of fetching the whole unfiltered table.
      const tierParams = new URLSearchParams();
      if (equipmentId) {
        tierParams.set("relatedTo", equipmentId);
      } else if (plantId) {
        tierParams.set("plant", plantId);
      }

      try {
        const [typesRes, protocolsRes, equipmentRes, tierRes] =
          await Promise.all([
            api.get("/instrument-types/", { signal }),
            api.get("/communication-types/", { signal }),
            api.get("/equipment/?" + equipParams.toString(), { signal }),
            api.get("/tiers/?" + tierParams.toString(), { signal }),
          ]);

        if (signal.aborted) return;

        const allEquipment =
          equipmentRes.data?.results ?? equipmentRes.data ?? [];
        const allTiers = tierRes.data?.results ?? tierRes.data ?? [];

        setInstrumentTypes(typesRes.data?.results ?? typesRes.data ?? []);
        setCommProtocols(protocolsRes.data?.results ?? protocolsRes.data ?? []);

        // PLC items are identified by "AS" in their tag number (same table, split client-side)
        setPlcOptions(
          allEquipment.filter((eq) =>
            (eq.tagNumber ?? "").toUpperCase().includes("AS"),
          ),
        );
        // P&IDs are narrowed to "Process" type equipment so the dropdown
        // isn't cluttered with PLCs and every other equipment type sharing
        // the same table.
        setPidOptions(
          allEquipment.filter(
            (eq) => (eq.type_details?.name ?? "").toLowerCase() === "process systems",
          ),
        );
        setIoPanelOptions(allTiers);
      } catch (err) {
        if (signal.aborted) return;
        setOptionsError(true);
        toast({
          title: "Warning",
          description: "Could not load all dropdown options.",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      } finally {
        if (!signal.aborted) setLoadingOptions(false);
      }
    };

    fetchOptions();

    // Cancel in-flight requests when the modal closes or deps change
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plantId, equipmentId]);

  // ── Populate form & fetch images when the instrument changes ─────────────
  useEffect(() => {
    if (!stableInstrument) return;

    setValues({
      tagNumber: stableInstrument.tagNumber ?? "",
      instrumentType: stableInstrument.instrumentType ?? "",
      communicationProtocol: stableInstrument.communicationProtocol ?? "",
      description: stableInstrument.description ?? "",
      plc: stableInstrument.plc ?? "",
      pid: stableInstrument.pid ?? "",
      ioPanel: stableInstrument.ioPanel ?? "",
      card: stableInstrument.card ?? "",
      channel: stableInstrument.channel ?? "",
    });
    setErrors({});
    setFileList([]);
    setInstrumentImages([]);
    setFlags([]);
    // Already nested on the instrument object (InstrumentsSerializer always
    // includes these) - no extra fetch needed, unlike the images/flags below.
    setRelatedEquipment(stableInstrument.relatedEquipment_details ?? []);
    setRelatedTiers(stableInstrument.relatedTiers_details ?? []);
    setRelatedComponents(stableInstrument.relatedComponents_details ?? []);

    const controller = new AbortController();

    api
      .get("/instrument-images/?instrument=" + stableInstrument.id, {
        signal: controller.signal,
      })
      .then((res) => {
        if (!controller.signal.aborted) {
          setInstrumentImages(res.data?.results ?? res.data ?? []);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error("Failed to load images", err);
        }
      });

    api
      .get("/instruments/" + stableInstrument.id + "/", {
        signal: controller.signal,
      })
      .then((res) => {
        if (!controller.signal.aborted) {
          setFlags(res.data.flags || []);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error("Failed to load instrument flags", err);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableInstrument?.id]);

  const set = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = () => {
    setErrors({});
    setFileList([]);
    onClose();
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await instrumentSchema.validate(values, { abortEarly: false });

      const payload = {
        tagNumber: values.tagNumber,
        description: values.description || "",
        instrumentType: values.instrumentType || null,
        communicationProtocol: values.communicationProtocol || null,
        plc: values.plc || null,
        pid: values.pid || null,
        ioPanel: values.ioPanel || null,
        card: values.card || "",
        channel: values.channel || "",
        site: siteId ? parseInt(siteId) : null,
        plant: plantId ? parseInt(plantId) : null,
        equipment: equipmentId ? parseInt(equipmentId) : null,
        tier: tierId ? parseInt(tierId) : null,
        component: componentId ? parseInt(componentId) : null,
        relatedEquipment: relatedEquipment.map((item) => item.id),
        relatedTiers: relatedTiers.map((item) => item.id),
        relatedComponents: relatedComponents.map((item) => item.id),
      };

      await api.patch("/instruments/" + stableInstrument.id + "/", payload);

      toast({
        title: "Success",
        description: "Instrument updated successfully!",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      onSuccess?.();
      handleClose();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      } else {
        const detail = error?.response?.data
          ? JSON.stringify(error.response.data)
          : "Failed to update instrument.";
        toast({
          title: "Error",
          description: detail,
          status: "error",
          duration: 6000,
          isClosable: true,
          position: "bottom",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDriveFlagToggle = async (checked) => {
    const driveFlag = flags.find((flag) => flag.name === "drive");

    try {
      if (checked) {
        const res = await api.post("/flags/", { instrument: stableInstrument.id, name: "drive" });
        setFlags((prev) => [...prev, res.data]);
      } else if (driveFlag) {
        await api.delete(`/flags/${driveFlag.id}/`);
        setFlags((prev) => prev.filter((flag) => flag.id !== driveFlag.id));
      }
    } catch (error) {
      const isForbidden = error?.response?.status === 403;
      toast({
        title: "Error",
        description: isForbidden
          ? "You don't have permission to change flags."
          : "Failed to update the Drive flag.",
        status: "error", duration: 4000, isClosable: true, position: "bottom",
      });
    }
  };

  const handleDeleteImage = async (id) => {
    try {
      await api.delete("/instrument-images/" + id + "/");
      setInstrumentImages((prev) => prev.filter((img) => img.id !== id));
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  const handleDownloadImage = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "image.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview)
      file.preview = await getBase64(file.originFileObj);
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const uploadImage = async (file) => {
    const uid = crypto.randomUUID();
    setUploadingImages((prev) => [...prev, { uid, progress: 0 }]);

    const formData = new FormData();
    formData.append("instrument", stableInstrument.id);
    formData.append("image", file);

    try {
      const res = await api.post("/instrument-images/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const progress = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
          setUploadingImages((prev) => prev.map((u) => (u.uid === uid ? { ...u, progress } : u)));
        },
      });
      setInstrumentImages((prev) => [...prev, res.data]);
    } finally {
      setUploadingImages((prev) => prev.filter((u) => u.uid !== uid));
    }
  };

  const createInstrumentType = async (name) => {
    try {
      const response = await api.post("/instrument-types/", { name });
      const newType = response.data;
      setInstrumentTypes((prev) => [...prev, newType]);
      return newType;
    } catch (error) {
      console.error("Error creating instrument type:", error);
      return null;
    }
  };

  const createCommunicationProtocol = async (name) => {
    try {
      const response = await api.post("/communication-types/", { name });
      const newProtocol = response.data;
      setCommProtocols((prev) => [...prev, newProtocol]);
      return newProtocol;
    } catch (error) {
      console.error("Error creating communication protocol:", error);
      return null;
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };

  const { canUploadMore, remainingSlots, limitLabel } = getImageUploadLimitState(
    instrumentImages.length + uploadingImages.length,
    MAX_GALLERY_IMAGES,
  );

  return (
    <Modal open={open} onClose={handleClose} sx={{ zIndex: 1000 }}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: 680 },
          bgcolor: "background.paper",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header — always rendered, never blanks during close animation */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2.5,
            bgcolor: "primary.main",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}
          >
            Edit Instrument — {stableInstrument?.tagNumber}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            overflowY: "auto",
            flexGrow: 1,
            px: 3,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "grey.100" },
          }}
        >
          {loadingOptions ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 2,
              }}
            >
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">
                Loading form options…
              </Typography>
            </Box>
          ) : (
            <>
              {/* Soft inline warning if options partially failed */}
              {optionsError && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: "8px",
                    bgcolor: "warning.light",
                    border: "1px solid",
                    borderColor: "warning.main",
                  }}
                >
                  <Typography variant="caption" color="warning.dark">
                    Some dropdown options couldn't be loaded. You can still
                    update other fields.
                  </Typography>
                </Box>
              )}

              {/* ── Identification ── */}
              <SectionLabel>Identification</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Tag Number *"
                    value={values.tagNumber}
                    onChange={set("tagNumber")}
                    fullWidth
                    error={!!errors.tagNumber}
                    helperText={errors.tagNumber}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={instrumentTypes}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      instrumentTypes.find(
                        (t) => t.id === values.instrumentType,
                      ) || null
                    }
                    filterOptions={(options, params) => {
                      const filtered = options.filter((o) =>
                        o.name
                          .toLowerCase()
                          .includes(params.inputValue.toLowerCase()),
                      );
                      if (
                        params.inputValue.trim() !== "" &&
                        filtered.length === 0
                      ) {
                        filtered.push({
                          id: "__new__",
                          name: `Add "${params.inputValue.trim()}"`,
                          inputValue: params.inputValue.trim(),
                        });
                      }
                      return filtered;
                    }}
                    onChange={async (_, selected) => {
                      if (!selected) {
                        setValues((prev) => ({ ...prev, instrumentType: "" }));
                        return;
                      }
                      if (selected.id === "__new__") {
                        const created = await createInstrumentType(
                          selected.inputValue,
                        );
                        if (created) {
                          setValues((prev) => ({
                            ...prev,
                            instrumentType: created.id,
                          }));
                        }
                      } else {
                        setValues((prev) => ({
                          ...prev,
                          instrumentType: selected.id,
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Instrument Type"
                        fullWidth
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* ── Related Assets ── */}
              <SectionLabel>Related Assets</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <EquipmentAutocomplete
                    multiple
                    plantId={plantId}
                    value={relatedEquipment}
                    onChange={setRelatedEquipment}
                    label="Related Equipment"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TierAutocomplete
                    multiple
                    plantId={plantId}
                    value={relatedTiers}
                    onChange={setRelatedTiers}
                    label="Related Tiers"
                  />
                </Grid>
                <Grid item xs={12}>
                  <PanelComponentAutocomplete
                    multiple
                    plantId={plantId}
                    value={relatedComponents}
                    onChange={setRelatedComponents}
                    label="Related Components"
                  />
                </Grid>
              </Grid>

              {/* ── IO Mapping ── */}
              <SectionLabel>IO Mapping</SectionLabel>
              <Grid container spacing={2}>
                {/* IO Panel */}
                <Grid item xs={6}>
                  <Autocomplete
                    options={ioPanelOptions}
                    getOptionLabel={(option) =>
                      option.name ?? option.tagNumber ?? String(option.id)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      ioPanelOptions.find((t) => t.id === values.ioPanel) ||
                      null
                    }
                    onChange={(_, selected) =>
                      setValues((prev) => ({
                        ...prev,
                        ioPanel: selected ? selected.id : "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="IO Panel"
                        fullWidth
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>

                {/* Card */}
                <Grid item xs={6}>
                  <TextField
                    label="Card"
                    value={values.card}
                    onChange={set("card")}
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>

                {/* Channel */}
                <Grid item xs={6}>
                  <TextField
                    label="Channel"
                    value={values.channel}
                    onChange={set("channel")}
                    fullWidth
                    sx={fieldSx}
                  />
                </Grid>

                {/* PLC */}
                <Grid item xs={6}>
                  <Autocomplete
                    options={plcOptions}
                    getOptionLabel={(option) =>
                      option.name ??
                      option.tagNumber ??
                      option.tag ??
                      String(option.id)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      plcOptions.find((eq) => eq.id === values.plc) || null
                    }
                    onChange={(_, selected) =>
                      setValues((prev) => ({
                        ...prev,
                        plc: selected ? selected.id : "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="PLC"
                        fullWidth
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>

                {/* P&ID */}
                <Grid item xs={6}>
                  <Autocomplete
                    options={pidOptions}
                    getOptionLabel={(option) =>
                      option.name ??
                      option.tagNumber ??
                      option.tag ??
                      String(option.id)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      pidOptions.find((eq) => eq.id === values.pid) || null
                    }
                    onChange={(_, selected) =>
                      setValues((prev) => ({
                        ...prev,
                        pid: selected ? selected.id : "",
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="P&ID"
                        fullWidth
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* ── Communication ── */}
              <SectionLabel>Communication</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={commProtocols}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={
                      commProtocols.find(
                        (p) => p.id === values.communicationProtocol,
                      ) || null
                    }
                    filterOptions={(options, params) => {
                      const filtered = options.filter((o) =>
                        o.name
                          .toLowerCase()
                          .includes(params.inputValue.toLowerCase()),
                      );
                      if (
                        params.inputValue.trim() !== "" &&
                        filtered.length === 0
                      ) {
                        filtered.push({
                          id: "__new__",
                          name: `Add "${params.inputValue.trim()}"`,
                          inputValue: params.inputValue.trim(),
                        });
                      }
                      return filtered;
                    }}
                    onChange={async (_, selected) => {
                      if (!selected) {
                        setValues((prev) => ({
                          ...prev,
                          communicationProtocol: "",
                        }));
                        return;
                      }
                      if (selected.id === "__new__") {
                        const created = await createCommunicationProtocol(
                          selected.inputValue,
                        );
                        if (created) {
                          setValues((prev) => ({
                            ...prev,
                            communicationProtocol: created.id,
                          }));
                        }
                      } else {
                        setValues((prev) => ({
                          ...prev,
                          communicationProtocol: selected.id,
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Communication Protocol"
                        fullWidth
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={values.description}
                    onChange={set("description")}
                    fullWidth
                    multiline
                    rows={3}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>

              {/* ── Flags ── */}
              <SectionLabel>Flags</SectionLabel>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={flags.some((flag) => flag.name === "drive")}
                    onChange={(event) => handleDriveFlagToggle(event.target.checked)}
                  />
                )}
                label="Drive"
              />

              {/* ── Images ── */}
              <SectionLabel>Images</SectionLabel>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "grey.50",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {instrumentImages.map((img) => (
                  <Box
                    key={img.id}
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                    }}
                  >
                    <Image
                      src={img.image_url}
                      width={100}
                      height={100}
                      style={{ objectFit: "cover" }}
                    />
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: "delete",
                            label: "Delete",
                            icon: <DeleteOutlined style={{ color: "red" }} />,
                            onClick: () => handleDeleteImage(img.id),
                          },
                          {
                            key: "download",
                            label: "Download",
                            icon: <DownloadOutlined />,
                            onClick: () => handleDownloadImage(img.image_url),
                          },
                        ],
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                      overlayStyle={{ zIndex: 3000 }}
                    >
                      <IconButton
                        sx={{
                          position: "absolute",
                          bottom: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.9)",
                          borderRadius: "50%",
                          p: 0.5,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          "&:hover": { bgcolor: "#fff" },
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Dropdown>
                  </Box>
                ))}

                {uploadingImages.map((u) => (
                  <UploadingImageTile key={u.uid} size={120} progress={u.progress} />
                ))}

                {canUploadMore && (
                  <Upload
                    listType="picture-card"
                    capture="environment"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={({ fileList: newList }) => setFileList(newList)}
                    maxCount={remainingSlots}
                    beforeUpload={createLimitedBeforeUpload({
                      getCurrentCount: () => instrumentImages.length + uploadingImages.length,
                      limit: MAX_GALLERY_IMAGES,
                      onLimitExceeded: () =>
                        toast({
                          title: "Limit reached",
                          description: `You can upload up to ${MAX_GALLERY_IMAGES} images.`,
                          status: "warning",
                          duration: 4000,
                          isClosable: true,
                          position: "bottom",
                        }),
                    })}
                    multiple
                    customRequest={async ({
                      file,
                      onSuccess: onOk,
                      onError,
                    }) => {
                      try {
                        await uploadImage(file);
                        onOk("ok");
                      } catch (err) {
                        onError(err);
                      }
                    }}
                    showUploadList={false}
                  >
                    <button
                      style={{ border: 0, background: "none" }}
                      type="button"
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>
                        Upload{limitLabel && <div>{limitLabel}</div>}
                      </div>
                    </button>
                  </Upload>
                )}
              </Box>

              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    zIndex: 2000,
                    onVisibleChange: (v) => setPreviewOpen(v),
                    afterOpenChange: (v) => !v && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}

              {/* Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 1,
                }}
              >
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  style={{ minWidth: 120 }}
                >
                  {isLoading ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Update"
                  )}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

/** Reusable section label matching the existing style */
const SectionLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      color: "primary.main",
    }}
  >
    {children}
  </Typography>
);
